import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatValue, API } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import PlayerBadge from "@/components/PlayerBadge";
import PlayerHeadshot, { TeamPill, PosRank } from "@/components/PlayerHeadshot";
import { SentimentTag, RealTradesTag } from "@/components/SignalTags";
import DmeTag from "@/components/DmeTag";
import { Loader2, ArrowLeft, TrendingUp, TrendingDown } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import MiniTradeCalc from "@/components/MiniTradeCalc";

const FORMATS = [{ id: "1qb", label: "1QB" }, { id: "superflex", label: "Superflex" }, { id: "tep", label: "TE Premium" }];

export default function PublicPlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [fmt, setFmt] = useState("superflex");
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setD(null); setNotFound(false);
    api.get(`/public/players/${slug}`, { params: { format: fmt } })
      .then(r => setD(r.data)).catch(() => setNotFound(true));
  }, [slug, fmt]);

  if (notFound) return (
    <PublicShell>
      <div className="card-bordered p-10 text-center">
        <p className="text-[var(--text-2)] mb-4">We couldn't find that player.</p>
        <Link to="/players" className="btn-primary inline-flex">Browse all players</Link>
      </div>
    </PublicShell>
  );
  if (!d) return <PublicShell><div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div></PublicShell>;

  const fmtLabel = FORMATS.find(f => f.id === fmt)?.label || fmt;
  const title = `${d.name} Dynasty Value (${fmtLabel}) — Trade Value & Rankings | Dynasty Metrics`;
  const desc = `${d.name} (${d.position}, ${d.team || "FA"}) dynasty trade value is ${formatValue(d.dme)} in ${fmtLabel} leagues — proprietary, age-adjusted DME valuation. Compare market sentiment vs real-trade data and build a trade.`;
  const url = `https://dynastymetrics.com/players/${slug}`;
  const ogImage = `${API}/og/players/${slug}.png?format=${fmt}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": d.name,
    "jobTitle": `${d.position} — ${d.team || "Free Agent"}`,
    "url": url,
    "description": desc,
    "mainEntityOfPage": url,
  };
  const breadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Player Values", "item": "https://dynastymetrics.com/players" },
      { "@type": "ListItem", "position": 2, "name": d.name, "item": url },
    ],
  };

  return (
    <PublicShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Link to="/players" className="inline-flex items-center gap-1.5 text-sm text-[var(--text-3)] hover:text-white mb-4" data-testid="public-player-back"><ArrowLeft className="w-4 h-4" /> All players</Link>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="card-bordered p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-4 min-w-0">
                <PlayerHeadshot playerId={d.player_id} name={d.name} team={d.team} size={72} thumb={false} className="border border-[var(--border)]" />
                <div className="min-w-0">
                  <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-2" data-testid="public-player-h1">
                    {d.name}<PlayerBadge badge={d.badge} size={22} />
                  </h1>
                  <div className="flex items-center gap-2 mt-1.5">
                    <PosRank rank={d.pos_rank} delta={d.pos_rank_delta} onFilter={(pos) => nav(`/players?pos=${pos}`)} />
                    <TeamPill team={d.team} />
                    <span className="text-sm text-[var(--text-3)] tracking-widest">{d.position}{d.age ? ` · ${d.age} yrs` : ""}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 border border-[var(--border)] rounded-sm p-0.5">
                {FORMATS.map(f => (
                  <button key={f.id} onClick={() => setFmt(f.id)} data-testid={`public-player-fmt-${f.id}`}
                    className={`text-xs px-2.5 py-1.5 rounded-sm transition-colors ${fmt === f.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white"}`}>{f.label}</button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="card-bordered p-4">
                <div className="text-[9px] tracking-widest text-[var(--accent)] font-mono inline-flex items-center gap-1">DME VALUE <DmeTag /></div>
                <div className="font-mono text-3xl text-[var(--accent)] mt-1" data-testid="public-player-dme">{formatValue(d.dme)}</div>
              </div>
              <div className="card-bordered p-4">
                <div className="text-[9px] tracking-widest font-mono inline-flex items-center gap-1" style={{ color: "#EF4444" }}>SENTIMENT <SentimentTag /></div>
                <div className="font-mono text-2xl mt-1" style={{ color: "#EF4444" }}>{d.sentiment ? formatValue(d.sentiment) : "—"}</div>
              </div>
              <div className="card-bordered p-4">
                <div className="text-[9px] tracking-widest font-mono inline-flex items-center gap-1" style={{ color: "#9CA3AF" }}>REAL TRADES <RealTradesTag /></div>
                <div className="font-mono text-2xl mt-1" style={{ color: "#9CA3AF" }}>{d.trades ? formatValue(d.trades) : "—"}</div>
              </div>
            </div>

            {d.age_adjustment && (
              <div className="mt-4 rounded-sm border p-3 text-sm flex items-start gap-2"
                style={{ borderColor: d.age_adjustment.pct > 0 ? "rgba(0,255,65,0.3)" : "rgba(239,68,68,0.3)" }}
                data-testid="public-player-ageadj">
                {d.age_adjustment.pct > 0 ? <TrendingUp className="w-4 h-4 text-[var(--accent)] mt-0.5" /> : <TrendingDown className="w-4 h-4 text-[var(--negative)] mt-0.5" />}
                <div>
                  <span className="font-mono font-semibold" style={{ color: d.age_adjustment.pct > 0 ? "var(--accent)" : "var(--negative)" }}>
                    {d.age_adjustment.pct > 0 ? "+" : ""}{d.age_adjustment.pct}% DME age adjustment
                  </span>
                  <span className="text-[var(--text-2)]"> — {d.age_adjustment.reason}</span>
                </div>
              </div>
            )}

            {d.history?.length > 1 && (
              <div className="mt-5">
                <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-2">30-DAY DME TREND</div>
                <div style={{ height: 120 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={d.history}>
                      <YAxis domain={["dataMin", "dataMax"]} hide />
                      <Tooltip contentStyle={{ background: "#0D0D0D", border: "1px solid #222", fontSize: 12 }} formatter={(v) => formatValue(v)} labelFormatter={() => ""} />
                      <Line type="monotone" dataKey="value" stroke="var(--accent)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          <div className="card-bordered p-5 text-sm text-[var(--text-2)] leading-relaxed">
            <p>
              <strong className="text-white">{d.name}'s</strong> Dynasty Metrics value of <strong className="text-[var(--accent)]">{formatValue(d.dme)}</strong> in {fmtLabel} leagues
              is a proprietary blend of live crowd sentiment and observed real-trade data, then adjusted for dynasty age curves.
              {d.age_adjustment ? ` That includes a ${d.age_adjustment.pct > 0 ? "+" : ""}${d.age_adjustment.pct}% age adjustment.` : ""} Values re-price every day.
            </p>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="card-bordered p-6 text-center">
            <div className="font-display text-lg font-bold mb-1">Trade {d.name.split(" ")[0]}?</div>
            <p className="text-sm text-[var(--text-2)] mb-4">Run it through our free trade calculator with live values, verdicts & age-adjusted fairness.</p>
            <Link to="/login" className="btn-primary inline-flex w-full justify-center" data-testid="public-player-cta">Open Trade Calculator</Link>
            <Link to="/players" className="block mt-3 text-xs text-[var(--text-3)] hover:text-white">← Browse all player values</Link>
          </div>
          <div className="card-bordered p-5">
            <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-2">WHY DME?</div>
            <ul className="text-xs text-[var(--text-2)] space-y-1.5">
              <li>• Dual-market blend (sentiment + real trades)</li>
              <li>• Dynasty age curves baked in</li>
              <li>• Hype-resistant, outlier-smoothed</li>
              <li>• Re-priced daily from live data</li>
            </ul>
          </div>
        </aside>
      </div>

      {(d.same_position?.length > 0 || d.similar?.length > 0) && (
        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="public-player-related">
          {d.same_position?.length > 0 && (
            <RelatedSection title={`More ${d.position}s`} items={d.same_position} testid="related-same-position" />
          )}
          {d.similar?.length > 0 && (
            <RelatedSection title="Similar Trade Value" items={d.similar} testid="related-similar-value" />
          )}
        </div>
      )}

      {d.same_position?.length > 0 && (
        <div className="mt-6 card-bordered p-5" data-testid="public-player-h2h">
          <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-3">{d.name.toUpperCase()} HEAD-TO-HEAD</div>
          <div className="flex flex-wrap gap-2">
            {d.same_position.map(r => (
              <Link key={r.slug} to={`/compare/${slug}/vs/${r.slug}`}
                className="text-xs px-3 py-1.5 rounded-sm border border-[var(--border)] text-[var(--text-2)] hover:text-white hover:border-[var(--accent)]/40 transition-colors"
                data-testid={`h2h-link-${r.slug}`}>{d.name.split(" ").slice(-1)[0]} vs {r.name}</Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <MiniTradeCalc
          format={fmt}
          seedA={[{ player_id: d.player_id, name: d.name, position: d.position, team: d.team }]}
          kicker={`TRADE ${d.name.toUpperCase()}`}
          heading={`Trade ${d.name.split(" ")[0]} for… — free, no login`}
        />
      </div>
    </PublicShell>
  );
}

function RelatedSection({ title, items, testid }) {
  return (
    <div className="card-bordered p-5" data-testid={testid}>
      <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-3">{title.toUpperCase()}</div>
      <div className="space-y-1">
        {items.map(r => (
          <Link key={r.slug} to={`/players/${r.slug}`}
            className="flex items-center gap-3 px-2 py-2 rounded-sm hover:bg-white/5 transition-colors"
            data-testid={`related-link-${r.slug}`}>
            <PlayerHeadshot playerId={r.player_id} name={r.name} team={r.team} size={32} />
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate hover:text-[var(--accent)]">{r.name}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <PosRank rank={r.pos_rank} />
                <TeamPill team={r.team} />
                <span className="text-[10px] tracking-widest text-[var(--text-3)]">{r.position}</span>
              </div>
            </div>
            <span className="font-mono text-sm text-[var(--accent)]">{formatValue(r.dme)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
